import { Box, Flex, Text, Tooltip } from '@modusign/design-system';
import { Location } from 'history';
import { CSSProperties, forwardRef, ReactNode } from 'react';
import { match, NavLink, useLocation } from 'react-router-dom';

import EmptyIcon from '@app/components/Icons/EmptyIcon';
import { useGNBFoldHandle } from '@app/containers/GlobalNavigationBar/GNBFoldHandleContext';
import { useColorScheme } from '@app/hooks/useColorScheme';
import useGNBGuideActive from '@app/hooks/useGNBGuideActive';

interface GlobalNavigationBarMenuProps {
  children: ReactNode;
  leftIcon: ReactNode;
  leftIconActive?: ReactNode;
  rightIcon?: ReactNode;
  rightIconHovered?: ReactNode;
  style?: CSSProperties;
  to: string;
  newTab?: boolean;
  isActive?<Params extends { [K in keyof Params]?: string }>(
    match: match<Params> | null,
    location: Location
  ): boolean;
  onClick?: () => void;
  onMouseEnter?: () => void;
}

const GlobalNavigationBarMenu = forwardRef<HTMLLIElement, GlobalNavigationBarMenuProps>(
  (
    {
      children,
      leftIcon,
      leftIconActive,
      rightIcon,
      rightIconHovered,
      style,
      to,
      isActive,
      onClick,
      newTab,
      onMouseEnter,
      ...props
    },
    ref
  ) => {
    const location = useLocation();

    const firstPart = location.pathname.match(/\/(\w|-)+/g)?.[0];
    const firstTargetPart = to.match(/\/(\w|-)+/g)?.[0];
    const navLinkActive = !!(
      firstPart === firstTargetPart ||
      (!!isActive && isActive(null, location))
    );

    const { theme } = useColorScheme();
    const { folded } = useGNBFoldHandle();

    return (
      <Box
        as="li"
        sx={{
          '&:hover .right-icon': { display: 'none' },
          '&:hover .right-icon-hover': { display: 'flex' },
        }}
        {...props} // HTMLLIElement의 props
        px={folded ? 3 : 0}
        ref={ref}
        onMouseEnter={onMouseEnter}
      >
        <NavLink
          isActive={isActive}
          style={{ cursor: 'pointer' }}
          target={newTab ? '_blank' : undefined}
          to={{ pathname: to }}
          onClick={onClick}
        >
          <Flex
            borderRadius="4px"
            color={theme.gnb.item.color}
            px={folded ? 0 : 3}
            py={folded ? 0 : 4}
            style={style}
            sx={{
              span: {
                fontWeight: theme.gnb.item.fontWeight,
              },
              '&:hover': theme.gnb.item.hover,
              ...(navLinkActive && {
                ...theme.gnb.item.active,
                span: {
                  fontWeight: 700,
                },
              }),
            }}
            width="100%"
          >
            <GlobalNavigationBarMenuContent
              leftIcon={leftIcon}
              leftIconActive={leftIconActive}
              navLinkActive={navLinkActive}
              rightIcon={rightIcon}
              rightIconHovered={rightIconHovered}
            >
              {children}
            </GlobalNavigationBarMenuContent>
          </Flex>
        </NavLink>
      </Box>
    );
  }
);

function GlobalNavigationBarMenuContent({
  children,
  leftIcon,
  leftIconActive,
  navLinkActive,
  rightIcon,
  rightIconHovered,
}: {
  children: ReactNode;
  leftIcon?: ReactNode;
  leftIconActive?: ReactNode;
  navLinkActive?: boolean;
  rightIcon?: ReactNode;
  rightIconHovered?: ReactNode;
}) {
  const { folded } = useGNBFoldHandle();

  if (folded) {
    return (
      <FoldedGlobalNavigationBarMenuContent
        leftIcon={leftIcon}
        leftIconActive={leftIconActive}
        linkLabel={children}
        navLinkActive={navLinkActive}
      />
    );
  }
  return (
    <>
      <Box
        alignItems="center"
        as="div"
        color="inherit"
        display="flex"
        justifyContent="center"
        mr={4}
      >
        {navLinkActive ? leftIconActive ?? leftIcon ?? <EmptyIcon /> : leftIcon ?? <EmptyIcon />}
      </Box>
      <Text
        as="span"
        color="inherit"
        display="inline-block"
        fontSize="1.07em"
        sx={{
          height: `18px`,
          verticalAlign: 'middle',
        }}
      >
        {children}
      </Text>
      <Box flex="1" />
      <Flex alignItems="center" className="right-icon" height="20px">
        {rightIcon}
      </Flex>
      <Flex alignItems="center" className="right-icon-hover" display="none" height="20px">
        {rightIconHovered}
      </Flex>
    </>
  );
}

function FoldedGlobalNavigationBarMenuContent({
  leftIcon,
  leftIconActive,
  navLinkActive,
  linkLabel,
}: {
  leftIcon?: ReactNode;
  leftIconActive?: ReactNode;
  navLinkActive?: boolean;
  linkLabel: ReactNode;
}) {
  const isGuideActive = useGNBGuideActive();

  return (
    <Tooltip content={linkLabel} disabled={isGuideActive} placement="right" scale="sm">
      <Box alignItems="center" as="div" display="flex" justifyContent="center">
        {navLinkActive ? leftIconActive ?? leftIcon ?? <EmptyIcon /> : leftIcon ?? <EmptyIcon />}
      </Box>
    </Tooltip>
  );
}

export default GlobalNavigationBarMenu;
